import { useQuery, UseQueryResult } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { runBaseCheck } from "api/Repos/data-checks"
import type {
  BaseCheckOptions,
  BaseCheckResult,
} from "types/dataChecks.types"

/**
 * The routing-derived base check, run on demand.
 *
 * Unlike the cached provenance reads (`useProvenanceQueries`), this one is `enabled: false`
 * and starts only when the caller calls `refetch()`: the check is a multi-second engine job
 * over the whole data file, so it runs when the user asks for it and never on mount or on a
 * window focus. That makes it the first API read in the repo built this way — the other
 * on-demand checks (`useDatamapDrift`) are `useApiCommand` wrappers with no cache at all, and
 * this one wants the cache so a rendered report survives a tab switch. Copy THIS one, not
 * those, for the next on-demand cached read.
 *
 * The result is a report, not an error path — the endpoint answers HTTP 200 on every outcome
 * and carries `status`. `error` from this hook means the REQUEST failed (transport, auth, a
 * body that would not parse, or the 20-minute local wait cap); a check that could not run
 * arrives as `data` with a non-ok `status`.
 *
 * @param projectId - The project to check
 * @param owner - Owner hint (may be "" — resolved server-side for your own projects)
 * @param options - `variant` (one flow per call) and `includeHidden` (a presentation choice:
 *   it moves `cleanable`, no count). Read the ANSWER's `variant` to learn which flow the
 *   engine actually walked — an omitted `variant` means "the questionnaire's own first".
 * @returns The react-query result. `refetch()` runs the check; `isFetching` is the spinner.
 *
 * @example
 * const { data, isFetching, refetch } = useBaseCheck(projectId, owner, { variant })
 * <Button loading={isFetching} onClick={() => refetch()}>Run check</Button>
 */
export const useBaseCheck = (
  projectId: string,
  owner: string,
  options: BaseCheckOptions = {}
): UseQueryResult<BaseCheckResult> => {
  const getAxios = useAuthedAxios()
  const { variant, includeHidden } = options

  return useQuery({
    // `include_hidden` is out of the key on purpose (see queryKeys.dataChecks): toggling it
    // and re-running overwrites this entry with the same counts and a moved `cleanable`.
    queryKey: queryKeys.dataChecks.baseCheck(projectId, variant ?? ""),
    // On demand only: the caller drives this with refetch().
    enabled: false,
    // Defence in depth rather than the thing that stops the refetches — `enabled: false`
    // already does that. This keeps the report from being treated as stale if the query is
    // ever enabled, mounted a second time or refetched by a cache-wide sweep: a report is a
    // statement about one questionnaire and one data file at one moment, so nothing but the
    // user asking again should re-run a job measured in seconds.
    //
    // What DOES bound the cached report is `gcTime`, left at its 5-minute default: the
    // result survives an unmounted route for five minutes, not indefinitely. A user who
    // navigates away and comes back later gets an empty panel and re-runs the check.
    staleTime: Infinity,
    // A failed check is a thing to show, not to retry: the expensive ones are exactly the
    // ones a retry storm would hurt.
    retry: false,
    meta: { errorTitle: "Base check failed" },
    queryFn: async (): Promise<BaseCheckResult> =>
      runBaseCheck(await getAxios(), projectId, owner, {
        variant,
        includeHidden,
      }),
  })
}

export default useBaseCheck
